import React, { useEffect, useState, useCallback, useRef } from "react";
import { GoogleMap, MarkerF, useLoadScript } from "@react-google-maps/api";

const GOOGLE_MAP_LIBRARIES = ["places"];

export function BookingSummary({
  selectedPractice,
  selectedLocation,
  practices = [],
}) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyByjBdnclLYyD29eTgLlI5ajoHvA_Wn8xI",
    libraries: GOOGLE_MAP_LIBRARIES,
  });

  const [markers, setMarkers] = useState([]);
  const geocodeCacheRef = useRef(new Map()); // address -> { lat, lng }
  const mapRef = useRef(null);

  // Helper to turn a location object into a geocodable string
  const formatAddress = (loc) => {
    const parts = [
      loc.address1,
      loc.address2,
      loc.city,
      loc.province || "Western Cape",
      loc.postalCode,
      "South Africa",
    ];
    return parts.filter(Boolean).join(", ");
  };

  useEffect(() => {
    console.log("Map load state:", { isLoaded, loadError });
    console.log("Practices for map:", practices);
  }, [isLoaded, loadError, practices]);

  useEffect(() => {
    if (!isLoaded) return; // wait until Maps JS is loaded
    if (!practices || practices.length === 0) {
      setMarkers([]);
      return;
    }

    // 1️⃣ Build a flat list of "raw" locations from practices
    const rawLocations = practices.flatMap((p) => {
      if (Array.isArray(p.locations) && p.locations.length > 0) {
        return p.locations.map((loc) => ({
          practiceNumber: p.practiceNumber,
          practiceName: p.practiceName,
          name: loc.name || p.primaryLocationName || "Main Location",
          address1: loc.address1 || p.practiceAddress,
          address2: loc.address2 || p.practiceAddress2,
          city: loc.city || p.practiceCity,
          province: loc.province || p.practiceProvince,
          postalCode: loc.postalCode || p.practicePostalCode,
        }));
      }

      // Fallback: main practice address
      return [
        {
          practiceNumber: p.practiceNumber,
          practiceName: p.practiceName,
          name: p.primaryLocationName || "Main Location",
          address1: p.practiceAddress,
          address2: p.practiceAddress2,
          city: p.practiceCity,
          province: p.practiceProvince,
          postalCode: p.practicePostalCode,
        },
      ];
    });

    console.log("All raw locations for map:", rawLocations);

    // 2️⃣ Filter by selection
    let candidates = rawLocations;

    // Practice + specific location selected → just that one
    if (selectedPractice && selectedLocation) {
      candidates = rawLocations.filter(
        (loc) =>
          loc.practiceNumber === selectedPractice.practiceNumber &&
          (!selectedLocation.name || loc.name === selectedLocation.name)
      );

      // Fallback: if nothing matched name, show all locations for that practice
      if (candidates.length === 0) {
        candidates = rawLocations.filter(
          (loc) => loc.practiceNumber === selectedPractice.practiceNumber
        );
      }
    } else if (selectedPractice) {
      // Only practice selected → all its locations
      candidates = rawLocations.filter(
        (loc) => loc.practiceNumber === selectedPractice.practiceNumber
      );
    }

    console.log("Candidate locations for geocoding:", candidates);

    let cancelled = false;

    async function geocodeAll() {
      if (!window.google || !window.google.maps) return;

      const geocoder = new window.google.maps.Geocoder();
      const newMarkers = [];

      // Promisify geocode
      const geocodeOnce = (address) =>
        new Promise((resolve, reject) => {
          geocoder.geocode({ address }, (results, status) => {
            if (status === "OK" && results && results[0]) {
              resolve(results[0]);
            } else {
              reject(status);
            }
          });
        });

      for (const loc of candidates) {
        const fullAddress = formatAddress(loc);
        if (!fullAddress) continue;

        // cache check
        if (geocodeCacheRef.current.has(fullAddress)) {
          const cached = geocodeCacheRef.current.get(fullAddress);
          newMarkers.push({
            ...loc,
            lat: cached.lat,
            lng: cached.lng,
            isFallback: false,
          });
          continue;
        }

        let geocodeSuccessful = false;
        const attempts = [
          { address: fullAddress, type: "Full Address" },
          {
            address: [loc.city, loc.province || "Western Cape", "South Africa"]
              .filter(Boolean)
              .join(", "),
            type: "City/Region",
          },
        ];

        for (const attempt of attempts) {
          if (geocodeSuccessful) break;
          if (!attempt.address) continue;

          try {
            const result = await geocodeOnce(attempt.address);
            const location = result.geometry?.location;
            if (!location) continue;

            const coords = { lat: location.lat(), lng: location.lng() };

            geocodeCacheRef.current.set(fullAddress, coords);

            newMarkers.push({
              ...loc,
              lat: coords.lat,
              lng: coords.lng,
              isFallback: attempt.type !== "Full Address",
            });

            geocodeSuccessful = true;
            console.log(
              `Geocode successful (${attempt.type}) for:`,
              attempt.address
            );
          } catch (status) {
            console.warn(
              `Geocode failed (${attempt.type}) for:`,
              attempt.address,
              status
            );
          }
        }

        if (!geocodeSuccessful) {
          console.error("All geocoding attempts failed for:", fullAddress);
        }
      }

      if (!cancelled) {
        console.log("Final markers:", newMarkers);
        setMarkers(newMarkers);
      }
    }

    geocodeAll();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, practices, selectedPractice, selectedLocation]);

  // 🔁 Auto-fit / zoom whenever markers change
  useEffect(() => {
    if (!mapRef.current || !markers.length || !window.google) return;

    const map = mapRef.current;
    const bounds = new window.google.maps.LatLngBounds();

    markers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));

    if (markers.length === 1) {
      map.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
      map.setZoom(15);
    } else {
      map.fitBounds(bounds);
    }
  }, [markers]);

  // 🗺 Initial map load handler – store ref + first fit
  const handleMapLoad = useCallback(
    (mapInstance) => {
      mapRef.current = mapInstance;

      if (!markers.length || !window.google) return;

      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((m) => bounds.extend({ lat: m.lat, lng: m.lng }));

      if (markers.length === 1) {
        mapInstance.setCenter({ lat: markers[0].lat, lng: markers[0].lng });
        mapInstance.setZoom(15);
      } else {
        mapInstance.fitBounds(bounds);
      }
    },
    [markers]
  );

  // ---------- Render states ----------
  if (loadError) return <p>Error loading maps</p>;
  if (!isLoaded) return <p>Loading map…</p>;
  if (!markers.length) return <p>No map data</p>;

  const defaultCenter = { lat: markers[0].lat, lng: markers[0].lng };

  return (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height: "400px" }}
      center={defaultCenter}
      zoom={12}
      onLoad={handleMapLoad}
    >
      {markers.map((m, idx) => (
        <MarkerF
          key={`${m.practiceNumber || "p"}-${m.name || "loc"}-${idx}`}
          position={{ lat: m.lat, lng: m.lng }}
          title={
            m.name
              ? `${m.practiceName || ""} – ${m.name}${
                  m.isFallback ? " (approximate)" : ""
                }`
              : m.practiceName || ""
          }
        />
      ))}
    </GoogleMap>
  );
}
